import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

const StateList = () => {
  const navigate = useNavigate();
  const [selectedState, setSelectedState] = useState('');
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get('/api/state_abbreviations/');
        setStates(response.data.states);
      } catch (error) {
        console.error('Error fetching states:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStates();
  }, []);

  const handleStateChange = (event) => {
    setSelectedState(event.target.value);
  };

  const handleCalculate = () => {
    if (selectedState) {
      navigate(`/property_tax_calculator/${selectedState}`);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
          <Typography align="center">Loading states...</Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 3, md: 5 }, 
          borderRadius: 2,
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
        }}
      >
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          align="center" 
          fontWeight="700"
          sx={{ 
            color: '#2b3a63',
            mb: 3
          }}
        >
          Property Tax Calculators
        </Typography>
        
        <Typography 
          variant="subtitle1" 
          paragraph 
          align="center"
          color="text.secondary"
          sx={{ 
            maxWidth: 700, 
            mx: 'auto', 
            mb: 5,
            fontSize: '1.1rem'
          }}
        >
          Find out exactly how much you're paying in property taxes and compare rates across different locations
        </Typography>

        {/* National Average Calculator - Featured Section */}
        <Box sx={{ mb: 6 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 5 }, 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #304ffe, #7b68ee)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 10px 20px rgba(61, 99, 174, 0.2)'
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute', 
                top: -60, 
                right: -60, 
                width: 200, 
                height: 200, 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                zIndex: 0
              }} 
            />
            <Box 
              sx={{ 
                position: 'absolute', 
                bottom: -40, 
                left: -40, 
                width: 150, 
                height: 150, 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                zIndex: 0
              }} 
            />
            <Grid container spacing={4} alignItems="center" position="relative" zIndex={1}>
              <Grid item xs={12} md={8}>
                <Typography 
                  variant="h3" 
                  gutterBottom 
                  fontWeight="bold" 
                  sx={{ 
                    color: 'white',
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  National Average Calculator
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph
                  sx={{ 
                    fontSize: '1.1rem',
                    mb: 3,
                    opacity: 0.9
                  }}
                >
                  Want to see how your property taxes compare across the entire United States? 
                  Our national calculator uses data from all 50 states and D.C. to show you 
                  how your property tax burden stacks up against the national average.
                </Typography>

              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                <Button
                  component={Link}
                  to="/property_tax_calculator/national"
                  variant="contained"
                  size="large"
                  sx={{ 
                    py: 2, 
                    px: 5, 
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    backgroundColor: 'white',
                    color: '#304ffe',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
                    },
                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                  }}
                >
                  Calculate Now
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        <Divider sx={{ mb: 5 }} />

        {/* State Dropdown and Calculate Button */}
        <Box sx={{ maxWidth: 600, mx: 'auto', mb: 6 }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              mb: 4,
              color: '#2b3a63',
              fontWeight: 600,
              textAlign: 'center'
            }}
          >
            Select Your State
          </Typography>
          
          <Grid container spacing={2} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" sx={{ mb: { xs: 2, md: 0 }, minWidth: 300 }}>
                <InputLabel id="state-select-label">State</InputLabel>
                <Select
                  labelId="state-select-label"
                  id="state-select"
                  value={selectedState}
                  onChange={handleStateChange}
                  label="State"
                  sx={{ 
                    height: 56,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#d0d7de'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3d63ae'
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select a state</em>
                  </MenuItem>
                  {states.map((state) => (
                    <MenuItem 
                      key={state.abbr} 
                      value={state.abbr}
                      sx={{ 
                        py: 1.5,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(61, 99, 174, 0.08)'
                        }
                      }}
                    >
                      {state.name} ({state.abbr})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                onClick={handleCalculate}
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={!selectedState}
                sx={{ 
                  py: 1.75, 
                  fontWeight: 'bold',
                  backgroundColor: '#3d63ae',
                  '&:hover': {
                    backgroundColor: '#2a4780',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#a0a0a0'
                  }
                }}
              >
                Calculate Taxes
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default StateList; 